// Auth Store Module
import { createBaseModule } from './base'
import { authApi } from '@/services/api'

// Custom state for auth
const customState = {
  token: localStorage.getItem('token') || '',
  user: JSON.parse(localStorage.getItem('user')) || null,
  orgId: localStorage.getItem('orgId') || null,
  isAuthenticated: !!localStorage.getItem('token')
}

// Custom getters for auth
const customGetters = {
  // Authentication status
  isAuthenticated: state => !!state.token && !!state.user,
  
  // Current user info
  currentUser: state => state.user,
  currentOrganization: (state, getters, rootState) => {
    if (!state.user || !rootState.organizations.items.length) return null
    return rootState.organizations.items.find(org => org.id === state.user.org_id)
  },
  
  // Role-based permissions
  isUser: state => state.user && (state.user.role === 'user' || state.user.role_id === 'user'),
  isModerator: state => state.user && (state.user.role === 'moderator' || state.user.role_id === 'moderator'),
  isAdmin: state => state.user && (state.user.role === 'admin' || state.user.role_id === 'admin'),
  isSuperAdmin: state => state.user && (
    state.user.role === 'superadmin' || 
    state.user.role_id === 'superadmin' || 
    state.user.role === 'super_admin' || 
    state.user.role_id === 'super_admin'
  ),
  
  // Hierarchical permission checks
  isModeratorOrAbove: (state, getters) => getters.isModerator || getters.isAdmin || getters.isSuperAdmin,
  isAdminOrAbove: (state, getters) => getters.isAdmin || getters.isSuperAdmin,
  
  // Permission-based getters
  canManageUsers: (state, getters) => getters.isAdminOrAbove,
  canManageOrganizations: (state, getters) => getters.isAdminOrAbove,
  canCreateUsers: (state, getters) => getters.isAdminOrAbove,
  canDeleteOrganizations: (state, getters) => getters.isSuperAdmin,
  canManageRoles: (state, getters) => getters.isSuperAdmin,
  
  // User's role within their organization
  userRole: state => state.user ? (state.user.role || state.user.role_id) : null
}

// Custom mutations for auth
const customMutations = {
  SET_TOKEN(state, token) {
    state.token = token
    state.isAuthenticated = !!token
    if (token) {
      localStorage.setItem('token', token)
    } else {
      localStorage.removeItem('token')
    }
  },
  
  SET_USER(state, user) {
    state.user = user
    state.isAuthenticated = !!user && !!state.token
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
      // Set org_id from user data if available
      if (user.org_id) {
        state.orgId = user.org_id
        localStorage.setItem('orgId', user.org_id)
      }
    } else {
      localStorage.removeItem('user')
    }
  },
  
  SET_ORG_ID(state, orgId) {
    state.orgId = orgId
    if (orgId) {
      localStorage.setItem('orgId', orgId)
    } else {
      localStorage.removeItem('orgId')
    }
  },
  
  LOGOUT(state) {
    state.token = ''
    state.user = null
    state.orgId = null
    state.isAuthenticated = false
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('orgId')
  }
}

// Custom actions for auth
const customActions = {
  async login({ commit, dispatch }, credentials) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await authApi.login(credentials)
      
      if (response.token) {
        commit('SET_TOKEN', response.token)
        
        // Fetch user info after successful login
        const user = await authApi.me()
        commit('SET_USER', user)
        
        dispatch('showSuccess', 'Login successful', { root: true })
        
        return { token: response.token, user }
      } else {
        throw new Error('No token received from server')
      }
    } catch (error) {
      commit('LOGOUT')
      commit('SET_ERROR', error.message || 'Login failed')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async register({ commit, dispatch }, userData) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await authApi.register(userData)
      
      dispatch('showSuccess', 'Registration successful', { root: true })
      
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Registration failed')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchUser({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const user = await authApi.me()
      commit('SET_USER', user)
      
      return user
    } catch (error) {
      // If fetching user fails, likely token is invalid
      commit('LOGOUT')
      commit('SET_ERROR', error.message || 'Failed to fetch user')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async createUserDirect({ commit, dispatch }, userData) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await authApi.createUserDirect(userData)
      
      dispatch('showSuccess', 'User created successfully', { root: true })
      
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create user')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  logout({ commit, dispatch }) {
    commit('LOGOUT')
    // Clear all module states so next login doesn't see stale data
    dispatch('resetAll', null, { root: true })
    dispatch('showSuccess', 'Logged out successfully', { root: true })
  },
  
  // Initialize auth state (call on app startup)
  async initializeAuth({ commit, dispatch }) {
    const token = localStorage.getItem('token')
    if (token) {
      commit('SET_TOKEN', token)
      try {
        await dispatch('fetchUser')
      } catch (error) {
        // If user fetch fails, clear auth state
        dispatch('logout')
      }
    }
  }
}

// Create and export the auth module
export default createBaseModule('auth', authApi, {
  customState,
  customGetters,
  customMutations,
  customActions
})
